import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { LuPencil } from "react-icons/lu";
import { useConsentApi } from "../../store/api/hooks/consentApiHook";
import Button from "../elements/button";
import FormattedDate from "../elements/FormattedDate";
import { formatPhoneNumber } from "../PhoneInput/CustomPhoneInput";
import ConsentStatusBadge from "./ConsentStatusBadge";
import SmsConsentFields from "./SmsConsentFields";
import { consentStatusOf, isValidTenDigitPhone, toTenDigitPhone } from "./consentUtils";

interface SmsPreference {
  smsStatus?: string | null;
  optedIn?: boolean | null;
  mobileNumber?: string | null;
  lastModifiedAt?: string | null;
}

interface Draft {
  smsOptIn: boolean;
  phone: string;
}

/**
 * The optional "Communication Preferences" block: view, then Update consent →
 * checkbox + disclosure + mobile number → Save / Cancel. Shared by the Owner's
 * Agreements & Policies card and everyone else's My Notification Settings.
 */
const CommunicationPreferences: React.FC<{ enabled: boolean }> = ({ enabled }) => {
  const { t } = useTranslation("consent");
  const consentApi = useConsentApi();
  const [pref, setPref] = useState<SmsPreference | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);

  const load = useCallback(() => {
    setIsLoading(true);
    setIsError(false);
    consentApi.getSmsPreference(
      {},
      (resp: any) => {
        setPref(resp?.data ?? null);
        setIsLoading(false);
      },
      () => {
        setIsError(true);
        setIsLoading(false);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload every time the drawer opens so the card always shows the latest saved values.
  useEffect(() => {
    if (enabled) {
      setDraft(null);
      load();
    }
  }, [enabled, load]);

  const status = consentStatusOf(pref);
  const storedOptIn = status === "in";
  const storedPhone = toTenDigitPhone(pref?.mobileNumber);

  const startEdit = () => setDraft({ smsOptIn: storedOptIn, phone: formatPhoneNumber(storedPhone) });

  // Save only when something differs from what's stored, so "Not given" + untouched
  // stays unsaved instead of silently becoming "Opted out".
  const dirty =
    !!draft && (draft.smsOptIn !== storedOptIn || (draft.smsOptIn && toTenDigitPhone(draft.phone) !== storedPhone));
  const phoneValid = !draft?.smsOptIn || isValidTenDigitPhone(draft.phone);
  const showPhoneError = !!draft?.smsOptIn && !phoneValid && draft.phone !== "";

  const handleSave = () => {
    if (!draft) return;
    setIsSaving(true);
    consentApi.saveSmsPreference(
      { optedIn: draft.smsOptIn, mobileNumber: draft.smsOptIn ? toTenDigitPhone(draft.phone) : storedPhone || null },
      () => {
        setIsSaving(false);
        toast.success(t("prefs.updated"));
        setDraft(null);
        load(); // show the saved values as the server now holds them
      },
      (message: string) => {
        setIsSaving(false);
        toast.error(message || t("prefs.saveFailed"));
      }
    );
  };

  return (
    <div className="p-3 border border-gray-200 rounded-lg">
      <div className="flex items-center gap-2 mb-3">
        <h5 className="text-sm font-semibold text-gray-900">{t("sms.title")}</h5>
        <span className="px-2 py-0.5 text-[11px] font-medium text-gray-500 bg-gray-100 rounded-full">{t("optional")}</span>
      </div>

      {isLoading && !pref && (
        <div className="space-y-2.5">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center justify-between">
              <div className="w-2/5 h-3.5 rounded bg-gray-100 animate-pulse" />
              <div className="w-1/3 h-3.5 rounded bg-gray-100 animate-pulse" />
            </div>
          ))}
          <div className="h-9 rounded-lg bg-gray-100 animate-pulse" />
        </div>
      )}
      {isError && (
        <p className="text-xs text-red-600" role="alert">
          {t("prefs.loadError")}{" "}
          <button type="button" className="font-medium underline" onClick={load}>
            {t("retry")}
          </button>
        </p>
      )}

      {pref && !draft && (
        <>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-gray-500">{t("prefs.operationalText")}</span>
              <ConsentStatusBadge status={status} />
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-gray-500">{t("prefs.mobileNumber")}</span>
              <span className="text-right text-gray-900">
                {pref.mobileNumber ? formatPhoneNumber(toTenDigitPhone(pref.mobileNumber)) : "—"}
              </span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-gray-500">{t("prefs.lastUpdated")}</span>
              <span className="text-right text-gray-900">
                {pref.lastModifiedAt ? <FormattedDate date={pref.lastModifiedAt} format="monthDayYear" /> : "—"}
              </span>
            </div>
          </div>
          <Button theme="secondary" size="sm" className="w-full mt-3" onClick={startEdit}>
            <LuPencil size={14} /> {t("prefs.updateConsent")}
          </Button>
        </>
      )}

      {pref && draft && (
        <div className="space-y-3">
          <SmsConsentFields
            idPrefix="myprofile"
            compact
            checked={draft.smsOptIn}
            onCheckedChange={(checked) =>
              setDraft({ smsOptIn: checked, phone: checked ? draft.phone : formatPhoneNumber(storedPhone) })
            }
            phone={draft.phone}
            onPhoneChange={(phone) => setDraft({ ...draft, phone })}
            phoneError={showPhoneError ? t("sms.phoneInvalid") : undefined}
          />
          <div className="flex items-center gap-2 pt-1">
            <Button theme="secondary" size="sm" className="flex-1" disabled={isSaving} onClick={() => setDraft(null)}>
              {t("prefs.cancel")}
            </Button>
            <Button size="sm" className="flex-1" disabled={!dirty || !phoneValid} working={isSaving} onClick={handleSave}>
              {t("prefs.save")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunicationPreferences;
